import React from 'react';
import {
  ApartmentOutlined,
  BankOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  FileProtectOutlined,
  FileTextFilled,
  FileTextOutlined,
  FolderOpenOutlined,
  GlobalOutlined,
  HomeFilled,
  HomeOutlined,
  InteractionFilled,
  InteractionOutlined,
  LineChartOutlined,
  PieChartFilled,
  PieChartOutlined,
  PlusCircleFilled,
  PlusCircleOutlined,
  RocketFilled,
  RocketOutlined,
  SafetyCertificateOutlined,
  SecurityScanFilled,
  SecurityScanOutlined,
  SisternodeOutlined,
  TagFilled,
  TagOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { constants } from 'redpoints-front-components-rp10';
import type { MenuConfigItem } from 'redpoints-front-components-rp10/components/SideMenu/SideMenu';

import { translate } from '../helpers/languageHelper';
import { ROUTE_PATHS } from './constants';

const { MENU_ITEM_TYPE } = constants;

const JOURNEY_MENU = {
  type: MENU_ITEM_TYPE.SINGLE,
  icon: <RocketOutlined />,
  iconActive: <RocketFilled />,
  name: translate('journey.title'),
  path: ROUTE_PATHS.JOURNEY,
};

const HOME_MENU = {
  type: MENU_ITEM_TYPE.SINGLE,
  icon: <HomeOutlined />,
  iconActive: <HomeFilled />,
  name: translate('home.title'),
  path: ROUTE_PATHS.HOME,
};

const WORKSPACE_MENU = {
  key: 'workspace',
  type: MENU_ITEM_TYPE.GROUP,
  name: translate('workspace.title'),
  children: [
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <PlusCircleOutlined />,
      iconActive: <PlusCircleFilled />,
      name: translate('newIncident.title'),
      path: ROUTE_PATHS.NEW_INCIDENT,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <SecurityScanOutlined />,
      iconActive: <SecurityScanFilled />,
      name: translate('assetVerification.title'),
      path: ROUTE_PATHS.ASSET_VERIFICATION,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <InteractionOutlined />,
      iconActive: <InteractionFilled />,
      name: translate('infringementEvaluation.title'),
      path: ROUTE_PATHS.INFRINGEMENT_EVALUATION,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <CheckCircleOutlined />,
      iconActive: <CheckCircleFilled />,
      name: translate('validation.title'),
      path: ROUTE_PATHS.VALIDATION,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <ApartmentOutlined />,
      name: translate('database.title'),
      path: ROUTE_PATHS.DATABASE,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <TeamOutlined />,
      name: translate('actors.title'),
      path: ROUTE_PATHS.ACTORS,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <SafetyCertificateOutlined />,
      name: translate('enforcementAudit.title'),
      path: ROUTE_PATHS.ENFORCEMENT_AUDIT,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <PieChartOutlined />,
      iconActive: <PieChartFilled />,
      name: translate('successPlans.title'),
      path: ROUTE_PATHS.SUCCESS_PLANS,
    },
  ],
};

const DATA_INSIGHTS_MENU = {
  key: 'data&insights',
  type: MENU_ITEM_TYPE.GROUP,
  name: translate('dataAndInsights.title'),
  children: [
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <LineChartOutlined />,
      name: translate('results.title'),
      path: ROUTE_PATHS.RESULTS,
      children: [
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('summary.title'),
          path: ROUTE_PATHS.SUMMARY,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('socialMediaImpact.title'),
          path: ROUTE_PATHS.SOCIAL_MEDIA_IMPACT,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('antipiracySummary.title'),
          path: ROUTE_PATHS.ANTIPIRACY_SUMMARY,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('resultAnalysis.title'),
          path: ROUTE_PATHS.RESULT_ANALYSIS,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('nonEnforceable.title'),
          path: ROUTE_PATHS.NON_ENFORCEABLE,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('websiteInfringements.title'),
          path: ROUTE_PATHS.WEBSITE_INFRINGEMENTS,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('myLinksAndDocs.title'),
          path: ROUTE_PATHS.MY_LINKS_AND_DOCS,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('sellers.title'),
          path: ROUTE_PATHS.SELLERS,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('economicImpact.title'),
          path: ROUTE_PATHS.ECONOMIC_IMPACT,
        },
        {
          type: MENU_ITEM_TYPE.SINGLE,
          name: translate('customReports.title'),
          path: ROUTE_PATHS.CUSTOM_REPORTS,
        },
      ],
    },
  ],
};

const CONFIGURATION_MENU = {
  key: 'configuration',
  type: MENU_ITEM_TYPE.GROUP,
  name: translate('configuration.title'),
  children: [
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <UnorderedListOutlined />,
      name: translate('sellerManagement.title'),
      path: ROUTE_PATHS.SELLER_MANAGEMENT,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <TagOutlined />,
      iconActive: <TagFilled />,
      name: translate('tagsManagement.title'),
      path: ROUTE_PATHS.TAGS_MANAGEMENT,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <SisternodeOutlined />,
      iconActive: <SisternodeOutlined />,
      name: translate('ruleSuggestions.title'),
      path: ROUTE_PATHS.RULE_SUGGESTIONS,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <FileTextOutlined />,
      iconActive: <FileTextFilled />,
      name: translate('templates.title'),
      path: ROUTE_PATHS.TEMPLATES,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <FolderOpenOutlined />,
      name: translate('documents.title'),
      path: ROUTE_PATHS.DOCUMENTS,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <FileProtectOutlined />,
      name: translate('smartOnboarding.title'),
      path: ROUTE_PATHS.SMART_ONBOARDING,
    },
  ],
};

const ADDITIONAL_SOLUTIONS = {
  key: 'additionalSolutions',
  type: MENU_ITEM_TYPE.GROUP,
  name: translate('additionalSolutions.title'),
  children: [
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <GlobalOutlined />,
      name: translate('domainManagement.title'),
      path: ROUTE_PATHS.DOMAIN_MANAGEMENT,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <BankOutlined />,
      name: translate('revenueRecovery.title'),
      path: ROUTE_PATHS.REVENUE_RECOVERY,
    },
    {
      type: MENU_ITEM_TYPE.SINGLE,
      icon: <SecurityScanOutlined />,
      name: translate('ipOfflineServices.title'),
      path: ROUTE_PATHS.IP_OFFLINE_SERVICES,
    },
  ],
};

const menuConfig: MenuConfigItem[] = [
  JOURNEY_MENU,
  HOME_MENU,
  WORKSPACE_MENU,
  DATA_INSIGHTS_MENU,
  CONFIGURATION_MENU,
  ADDITIONAL_SOLUTIONS,
];

export default menuConfig;
